import * as React from 'react';
// @ts-expect-error
import {components, type OptionProps, type SingleValueProps} from 'react-select';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {CirclePlusRegularIcon} from 'shells/icon';
import {Stack} from 'shells/layout/stack/stack';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';
import {OptionWithIcon} from 'shells/select-option-components/option-with-icon';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {Body} from 'shells/typography/body';

import type {UserQuotaFragment} from 'nutshell-graphql-types';
import type {UserQuotaFragment_quotas as Quota} from 'nutshell-graphql-types';

import type {FilterOption} from '../../core/select/graphql-select-users';
import {GraphQLSelectUsers} from '../../core/select/graphql-select-users';

import {getCurrentFiscalYear} from './helpers';

const START_FROM_SCRATCH_VALUE = 'start-from-scratch';

type Props = {
    existingUsers: UserQuotaFragment[];
    onClose: () => void;
    onAddDraftUser: (user: UserQuotaFragment) => void;
    onDuplicateQuota: (userId: string, duplicateFromUserId: string) => Promise<any>;
    selectedFiscalYear: number;
};

const START_FROM_SCRATCH_OPTION: ListOptionWithIcon = {
    value: START_FROM_SCRATCH_VALUE,
    label: 'Start from scratch',
    icon: CirclePlusRegularIcon,
    iconColor: colors.grey,
};

export function AddQuotaModal(props: Props) {
    const [selectedUser, setSelectedUser] = React.useState<ListOptionWithIcon | null>(null);

    const [duplicateFromUser, setDuplicateFromUser] =
        React.useState<ListOptionWithIcon>(START_FROM_SCRATCH_OPTION);
    const [isSaving, setIsSaving] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const hasExistingUsers = props.existingUsers.length > 0;

    const existingUserIds = React.useMemo(
        () => new Set(props.existingUsers.map((u) => u.id)),
        [props.existingUsers]
    );

    const currentFiscalYear = getCurrentFiscalYear();

    const startFromOptions = React.useMemo(() => {
        return [
            START_FROM_SCRATCH_OPTION,
            ...props.existingUsers
                .filter((u) => u.quotas.some((q) => q.fiscalYear >= currentFiscalYear))
                .map((u) => ({
                    value: u.id,
                    label: u.name,
                    avatarUrl: u.avatarUrl,
                    initials: u.initials,
                })),
        ];
    }, [props.existingUsers, currentFiscalYear]);

    const handleClose = () => {
        setSelectedUser(null);
        setDuplicateFromUser(START_FROM_SCRATCH_OPTION);
        setErrorMessage(null);
        props.onClose();
    };

    const handleSubmit = () => {
        if (!selectedUser) return;

        if (duplicateFromUser && duplicateFromUser.value !== START_FROM_SCRATCH_VALUE) {
            setIsSaving(true);
            setErrorMessage(null);
            props
                .onDuplicateQuota(selectedUser.value, duplicateFromUser.value)
                .catch(() => {
                    setErrorMessage('Something went wrong. Please try again.');
                })
                .finally(() => {
                    setIsSaving(false);
                });
        } else {
            const draftUser: UserQuotaFragment = {
                __typename: 'User',
                id: selectedUser.value,
                name: selectedUser.label.replace(' (You)', ''),
                avatarUrl: selectedUser.avatarUrl ?? '',
                initials: selectedUser.initials ?? '',
                teams: [],
                quotas: [],
            };
            props.onAddDraftUser(draftUser);
            handleClose();
        }
    };

    // Autofocus doesn't work with ModalLightbox so we need to focus the input manually for now
    React.useEffect(() => {
        if (document.getElementById('add-quota-modal-select-users')) {
            document.getElementById('add-quota-modal-select-users')?.focus();
        }
    }, []);

    return (
        <ModalLightbox onClose={handleClose}>
            <Card
                hasBorder={true}
                hasShadow={false}
                backgroundColor='white'
                borderColor='white'
                overflowVisible={true}
            >
                <form
                    className='pad-24'
                    style={{width: 400}}
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <Stack spacing={16}>
                        <Body size='xlarge' isBold={true} color='dark'>
                            Add a quota for a user
                        </Body>
                        {!hasExistingUsers && (
                            <Body color='dark'>
                                After you create a quota, you can copy it for the rest of your team
                            </Body>
                        )}
                        <Stack spacing={8}>
                            <Body size='small' isBold={true}>
                                User
                            </Body>
                            <GraphQLSelectUsers
                                inputId='add-quota-modal-select-users'
                                selectedId={selectedUser?.value ?? null}
                                isMulti={false}
                                onChange={(option: ListOptionWithIcon) =>
                                    setSelectedUser(option ?? null)
                                }
                                onClearValue={() => setSelectedUser(null)}
                                filterOption={(option: FilterOption, inputValue: string) => {
                                    if (existingUserIds.has(option.data.value)) {
                                        return false;
                                    }

                                    if (!inputValue) {
                                        return true;
                                    }

                                    return option.data.label
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase());
                                }}
                                autoFocus={false}
                            />
                        </Stack>
                        {hasExistingUsers ? (
                            <Stack spacing={8}>
                                <Body color='dark' size='small' isBold={true}>
                                    Start from
                                </Body>
                                <SelectPickerGeneric
                                    options={startFromOptions}
                                    value={duplicateFromUser}
                                    placeholder='Select a user…'
                                    // @ts-expect-error
                                    onChange={(option) => setDuplicateFromUser(option)}
                                    components={{
                                        Option: OptionComponent,
                                        SingleValue: SingleValueComponent,
                                    }}
                                />
                                {selectedUser && duplicateFromUser && (
                                    <Body size='mini'>
                                        {getDuplicateFromDescription(
                                            selectedUser.label,
                                            duplicateFromUser,
                                            props.existingUsers,
                                            props.selectedFiscalYear
                                        )}
                                    </Body>
                                )}
                            </Stack>
                        ) : null}
                        {errorMessage ? (
                            <Body color='error' size='small'>
                                {errorMessage}
                            </Body>
                        ) : null}
                        <Stack isHorizontal={true} justifyContent='space-between'>
                            <Button
                                variant='text-secondary'
                                onClick={handleClose}
                                size='big'
                                noPadding={true}
                                disabled={isSaving}
                                type='button'
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='primary'
                                size='big'
                                type='submit'
                                disabled={
                                    !selectedUser ||
                                    (hasExistingUsers && !duplicateFromUser) ||
                                    isSaving
                                }
                            >
                                {getSubmitLabel(isSaving, hasExistingUsers)}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Card>
        </ModalLightbox>
    );
}

function getSubmitLabel(isSaving: boolean, hasExistingUsers: boolean): string {
    if (isSaving) return 'Adding…';

    return hasExistingUsers ? 'Add user' : 'Add quota';
}

const OptionComponent = (props: OptionProps) => {
    // This is a hack to get around limitations in our old version of flow
    const spreadableProps: OptionProps = props as any;

    return (
        <components.Option {...spreadableProps}>
            <OptionWithIcon
                label={props.data.label}
                icon={props.data.icon}
                iconColor={props.data.iconColor}
                avatarUrl={props.data.avatarUrl}
                initials={props.data.initials}
            />
        </components.Option>
    );
};

const SingleValueComponent = (props: SingleValueProps) => {
    return (
        <components.SingleValue {...props}>
            <OptionWithIcon
                label={props.data.label}
                icon={props.data.icon}
                iconColor={props.data.iconColor}
                avatarUrl={props.data.avatarUrl}
                initials={props.data.initials}
            />
        </components.SingleValue>
    );
};

function getDuplicateFromDescription(
    selectedUserName: string,
    duplicateFromUser: {
        value: string;
        label: string;
    },
    existingUsers: UserQuotaFragment[],
    selectedFiscalYear: number
): string {
    if (duplicateFromUser.value === START_FROM_SCRATCH_VALUE) {
        return `${selectedUserName}'s quota will be created for ${selectedFiscalYear}`;
    }

    const duplicateFromUserInfo: UserQuotaFragment | undefined = existingUsers.find(
        (user) => user.id === duplicateFromUser.value
    );

    if (!duplicateFromUserInfo) {
        return `${duplicateFromUser.label}'s quotas for each year will be added for ${selectedUserName}`;
    }

    const duplicateFromUserQuotas: Quota[] = duplicateFromUserInfo.quotas;
    const currentFiscalYear = getCurrentFiscalYear();

    const fiscalYearsToCopy: number[] = duplicateFromUserQuotas
        .filter((quota) => quota.fiscalYear >= currentFiscalYear)
        .map((quota) => quota.fiscalYear);

    return `${duplicateFromUser.label}'s ${fiscalYearsToCopy
        .sort((a, b) => a - b)
        .join(', ')} quota will be added for ${selectedUserName}`;
}
